/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import TextBlock from '@component/common/block/text-block';

type TCalloutDirection = 'top' | 'bottom' | 'left' | 'right';
type TCalloutSize = 'tiny' | 'small' | 'medium' | 'large';

interface IProps {
  text: string;
  direction?: TCalloutDirection;
  theme?: ETheme;
  custom?: {
    backgroundColor?: string;
    textColor?: string;
    borderColor?: string;
  };
  size?: TCalloutSize;
  width?: '100%' | 'fit-content' | string;
  height?: '100%' | 'auto' | string;
  animation?: boolean;
  handleClick?: () => void;
}

const createDesign = (
  calloutSize: TCalloutSize,
  theme: ETheme,
  direction: TCalloutDirection,
  animation: boolean,
  custom?: IProps['custom'],
) => {
  // ############### createSize ###############
  const createSize = () => {
    switch (calloutSize) {
      case 'tiny':
        return { padding: '4px 8px', textSize: 'tiny' as const, tailSize: 4, borderRadius: size.BORDER_RADIUS_1 };
      case 'small':
        return { padding: '6px 10px', textSize: 'small' as const, tailSize: 6, borderRadius: size.BORDER_RADIUS_1 };
      case 'large':
        return { padding: '14px 18px', textSize: 'large' as const, tailSize: 10, borderRadius: size.BORDER_RADIUS_2 };
      case 'medium':
      default:
        return { padding: '10px 14px', textSize: 'medium' as const, tailSize: 8, borderRadius: size.BORDER_RADIUS_1 };
    }
  };

  // ############### createTheme ###############
  const createTheme = () => {
    switch (theme) {
      case ETheme.CUSTOM:
        return {
          backgroundColor: custom?.backgroundColor ?? color.WHITE_1,
          textColor: custom?.textColor ?? color.BLACK_2,
          borderColor: custom?.borderColor ?? color.BLACK_2,
        };
      case ETheme.BLACK:
        return { backgroundColor: color.BLACK_2, textColor: color.WHITE_1, borderColor: color.BLACK_2 };
      case ETheme.BROWN:
        return { backgroundColor: color.BROWN_4, textColor: color.BROWN_1, borderColor: color.BROWN_2 };
      case ETheme.CREAM:
        return { backgroundColor: color.BROWN_5, textColor: color.BROWN_1, borderColor: color.BROWN_3 };
      case ETheme.GRAY:
        return { backgroundColor: color.WHITE_1, textColor: color.GRAY_1, borderColor: color.GRAY_2 };
      case ETheme.YELLOW:
        return { backgroundColor: color.YELLOW_5, textColor: color.BLACK_2, borderColor: color.YELLOW_2 };
      case ETheme.GREEN:
        return { backgroundColor: color.GREEN_5, textColor: color.GREEN_1, borderColor: color.GREEN_3 };
      case ETheme.RED:
        return { backgroundColor: color.RED_5, textColor: color.RED_1, borderColor: color.RED_3 };
      case ETheme.WHITE:
        return { backgroundColor: color.WHITE_1, textColor: color.BLACK_2, borderColor: color.BLACK_2 };
      default:
        return { backgroundColor: color.WHITE_1, textColor: color.BLACK_2, borderColor: color.GRAY_2 };
    }
  };

  // ############### createTail ###############
  const createTail = (tailSize: number, backgroundColor: string, borderColor: string) => {
    const innerSize = tailSize - 1;
    const halfOffset = `calc(50% - ${tailSize}px)`;
    const innerHalfOffset = `calc(50% - ${innerSize}px)`;
    const base = `content: ''; position: absolute; width: 0; height: 0;`;

    switch (direction) {
      case 'top':
        return `
          &::before { ${base} top: -${tailSize}px; left: ${halfOffset}; border-left: ${tailSize}px solid transparent; border-right: ${tailSize}px solid transparent; border-bottom: ${tailSize}px solid ${borderColor}; }
          &::after { ${base} top: -${innerSize}px; left: ${innerHalfOffset}; border-left: ${innerSize}px solid transparent; border-right: ${innerSize}px solid transparent; border-bottom: ${innerSize}px solid ${backgroundColor}; }
        `;
      case 'bottom':
        return `
          &::before { ${base} bottom: -${tailSize}px; left: ${halfOffset}; border-left: ${tailSize}px solid transparent; border-right: ${tailSize}px solid transparent; border-top: ${tailSize}px solid ${borderColor}; }
          &::after { ${base} bottom: -${innerSize}px; left: ${innerHalfOffset}; border-left: ${innerSize}px solid transparent; border-right: ${innerSize}px solid transparent; border-top: ${innerSize}px solid ${backgroundColor}; }
        `;
      case 'left':
        return `
          &::before { ${base} left: -${tailSize}px; top: ${halfOffset}; border-top: ${tailSize}px solid transparent; border-bottom: ${tailSize}px solid transparent; border-right: ${tailSize}px solid ${borderColor}; }
          &::after { ${base} left: -${innerSize}px; top: ${innerHalfOffset}; border-top: ${innerSize}px solid transparent; border-bottom: ${innerSize}px solid transparent; border-right: ${innerSize}px solid ${backgroundColor}; }
        `;
      case 'right':
        return `
          &::before { ${base} right: -${tailSize}px; top: ${halfOffset}; border-top: ${tailSize}px solid transparent; border-bottom: ${tailSize}px solid transparent; border-left: ${tailSize}px solid ${borderColor}; }
          &::after { ${base} right: -${innerSize}px; top: ${innerHalfOffset}; border-top: ${innerSize}px solid transparent; border-bottom: ${innerSize}px solid transparent; border-left: ${innerSize}px solid ${backgroundColor}; }
        `;
      default:
        return '';
    }
  };

  // ############### createAnimation ###############
  const createAnimation = () => {
    if (!animation) return '';
    return `
      @keyframes glow {
        0%, 100% { box-shadow: 0 0 4px rgba(0, 0, 0, 0.1); }
        50% { box-shadow: 0 0 12px rgba(0, 0, 0, 0.25); }
      }
      animation: glow 2s ease-in-out infinite;
    `;
  };

  // ############### createEvent ###############
  const createEvent = () => {
    return {
      cursor: 'pointer' as const,
      hover: `filter: brightness(0.95);`,
    };
  };

  // ############### Combine ###############
  const sizeDesign = createSize();
  const themeDesign = createTheme();
  const event = createEvent();

  return {
    // size
    padding: sizeDesign.padding,
    borderRadius: sizeDesign.borderRadius,
    textSize: sizeDesign.textSize,
    // theme
    backgroundColor: themeDesign.backgroundColor,
    textColor: themeDesign.textColor,
    borderColor: themeDesign.borderColor,
    shadow: color.SURFACE_SHADOW,
    // tail
    tailStyle: createTail(sizeDesign.tailSize, themeDesign.backgroundColor, themeDesign.borderColor),
    // animation
    animationStyle: createAnimation(),
    // event
    event,
  };
};

interface IWrapperProps {
  $design: ReturnType<typeof createDesign>;
  $width: string;
  $height: string;
  $clickable: boolean;
}

const Wrapper = styled.div<IWrapperProps>`
  position: relative;
  display: inline-block;
  width: ${(props) => props.$width};
  height: ${(props) => props.$height};
  padding: ${(props) => props.$design.padding};
  background-color: ${(props) => props.$design.backgroundColor};
  border: 1px solid ${(props) => props.$design.borderColor};
  border-radius: ${(props) => props.$design.borderRadius};
  box-shadow: ${(props) => props.$design.shadow};
  cursor: ${(props) => (props.$clickable ? props.$design.event.cursor : 'default')};

  &:hover {
    ${(props) => (props.$clickable ? props.$design.event.hover : '')}
  }

  ${(props) => props.$design.animationStyle}
  ${(props) => props.$design.tailStyle}
`;

const Callout: React.FC<IProps> = ({
  text,
  direction = 'top',
  theme = ETheme.BROWN,
  custom,
  size = 'medium',
  width = 'fit-content',
  height = 'auto',
  animation = false,
  handleClick,
}) => {
  const design = createDesign(size, theme, direction, animation, custom);

  return (
    <Wrapper
      $design={design}
      $width={width}
      $height={height}
      $clickable={!!handleClick}
      onClick={
        handleClick
          ? (e) => {
              e.stopPropagation();
              handleClick();
            }
          : undefined
      }>
      <TextBlock text={text} design={{ text: { size: design.textSize, color: design.textColor } }} />
    </Wrapper>
  );
};

export default Callout;
